import { useState } from "react";
import PageBanner from "@/components/feature/PageBanner";
import EmptyState from "@/components/base/EmptyState";
import { Skeleton } from "@/components/base/Skeleton";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/components/feature/AuthProvider";
import { useClub } from "@/hooks/useClub";
import { useToast } from "@/components/feature/ToastProvider";
import { apiPost } from "@/lib/api";
import { isApp, openBatterySettings } from "@/lib/mdlApp";
import type { StepRankResult } from "@/lib/types";
import { STEP_PERIODS, formatRange, type StepPeriodKey } from "./utils";
import StepMyBar from "./components/StepMyBar";
import StepRankList from "./components/StepRankList";

// /steps — 걸음 랭킹. 오늘 / 이번주 / 이번달 탭.
// 걸음 수는 앱이 올린 값만 집계한다. 웹은 조회만 한다.
export default function StepsPage() {
  const [period, setPeriod] = useState<StepPeriodKey>("today");
  const { data, loading, error, reload } = useApi<StepRankResult>(
    `step_rank.php?period=${period}&limit=100`
  );
  const { member } = useAuth();
  const { club } = useClub();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const app = club?.app;
  const appReady = Number(app?.has ?? 0) === 1 && !!app?.url;
  const me = data?.me ?? null;
  const isPublic = me ? Number(me.public ?? 0) === 1 : false;

  const togglePublic = async () => {
    if (!me || saving) return;
    const next = isPublic ? 0 : 1;
    setSaving(true);
    try {
      await apiPost(`step_me.php?act=public&v=${next}`, {});
      toast(
        next
          ? "걸음 랭킹에 이름을 공개했습니다."
          : "걸음 랭킹에서 이름을 비공개로 바꿨습니다.",
        "info"
      );
      reload();
    } catch (e) {
      toast((e as Error).message || "변경하지 못했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const players = Number(data?.players ?? 0);
  const rows = data?.list ?? [];

  return (
    <>
      <PageBanner
        title="🏃 걸음 랭킹"
        description="안드로이드 앱이 기록한 걸음 수로 겨루는 순위입니다. 오늘 · 이번주 · 이번달 중 골라 보세요."
        breadcrumb={[{ label: "걸음 랭킹" }]}
      />

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
        {/* 탭 + 기간 */}
        <div className="mb-5">
          <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
            {STEP_PERIODS.map((p) => {
              const on = period === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPeriod(p.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                    on
                      ? "bg-background-50 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-800"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          {!loading && !error && data && (
            <p className="mt-2.5 text-xs text-foreground-500">
              {formatRange(data.from, data.to)}
              {" · "}
              {players}명 참여
              {data.label ? ` · ${data.label}` : ""}
            </p>
          )}
        </div>

        {/* 내 순위 고정 바 */}
        {me && (
          <div className="mb-5">
            <StepMyBar me={me} saving={saving} onTogglePublic={togglePublic} />
          </div>
        )}

        {/* 목록 */}
        {loading ? (
          <div className="space-y-2.5 rounded-2xl border border-background-200 bg-background-50 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="랭킹을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="ri-footprint-line"
            title="아직 기록이 없습니다"
            description="앱을 설치하고 걸어 보세요!"
            action={
              appReady ? (
                <a
                  href={app?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-android-line" aria-hidden="true"></i>
                  앱 설치 안내
                </a>
              ) : undefined
            }
          />
        ) : (
          <StepRankList rows={rows} myNo={me?.mem_no} />
        )}

        {/* 하단 설정 · 안내 */}
        <div className="mt-10 space-y-4 border-t border-background-200 pt-6">
          {member && me && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-background-200 bg-background-50 px-4 py-3.5 md:px-5">
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  걸음 랭킹에 내 이름 공개
                </p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  끄면 목록에서 내 이름이 사라지지만 내 기록은 계속 쌓입니다.
                </p>
              </div>
              <button
                type="button"
                onClick={togglePublic}
                disabled={saving}
                aria-pressed={isPublic}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  isPublic ? "bg-primary-500" : "bg-background-300"
                }`}
              >
                <span
                  className={`absolute h-5 w-5 rounded-full bg-background-50 transition-transform ${
                    isPublic ? "translate-x-5" : "translate-x-0.5"
                  }`}
                ></span>
              </button>
            </div>
          )}

          <div className="space-y-1.5 px-1 text-xs text-foreground-500">
            {!isApp() && appReady && (
              <p className="flex flex-wrap items-center gap-2">
                <i className="ri-information-line" aria-hidden="true"></i>
                {app?.memo || "걸음 수는 안드로이드 앱에서 자동으로 기록됩니다"}
                <a
                  href={app?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary-700 hover:underline"
                >
                  앱 설치하기
                </a>
              </p>
            )}
            <p>걸음 수는 안드로이드 앱이 설치된 회원만 집계됩니다.</p>
            <p>폰 기본 만보계와 숫자가 조금 다를 수 있습니다.</p>
            {isApp() && (
              <p className="flex flex-wrap items-center gap-2">
                걸음이 늦게 반영되나요? 배터리 최적화에서 앱을 제외해 주세요.
                <button
                  type="button"
                  onClick={() => openBatterySettings()}
                  className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-primary-700 hover:underline"
                >
                  <i className="ri-settings-3-line" aria-hidden="true"></i>
                  설정 열기
                </button>
              </p>
            )}
          </div>
        </div>
      </section>
    </>
  );
}